import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Car, Search, MapPin, Star, ArrowLeft, Phone, Truck, Bike, Clock, Map as MapIcon, List, X, SlidersHorizontal } from 'lucide-react';
import axios from 'axios';
import TalleresMap from '../components/TalleresMap';

const VEHICLE_TYPE_META = {
  livianos: { label: 'Livianos', icon: Car },
  pesados: { label: 'Pesados', icon: Truck },
  motos: { label: 'Motos', icon: Bike },
};

const SORT_OPTIONS = [
  { value: 'rating', label: 'Mejor calificación' },
  { value: 'reviews', label: 'Más reseñas' },
  { value: 'name', label: 'Nombre A-Z' },
];

const RATING_OPTIONS = [
  { value: 'all', label: 'Cualquier calificación' },
  { value: '3', label: '3.0 o más ★' },
  { value: '4', label: '4.0 o más ★' },
  { value: '4.5', label: '4.5 o más ★' },
];

const SearchPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [talleres, setTalleres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vehicleTypeFilter, setVehicleTypeFilter] = useState('all');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('all');
  const [ratingFilter, setRatingFilter] = useState('all');
  const [sortBy, setSortBy] = useState('rating');
  const [hoveredId, setHoveredId] = useState(null);
  const [mobileView, setMobileView] = useState('list');

  useEffect(() => {
    fetchTalleres();
  }, [vehicleTypeFilter]);

  const fetchTalleres = async () => {
    try {
      setLoading(true);
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const params = {};
      if (vehicleTypeFilter !== 'all') params.vehicle_type = vehicleTypeFilter;
      const response = await axios.get(`${BACKEND_URL}/api/public/talleres`, { params });
      setTalleres(response.data);
    } catch (error) {
      console.error('Error fetching talleres:', error);
      setTalleres([]);
    } finally {
      setLoading(false);
    }
  };

  const serviceOptions = useMemo(() => {
    const set = new Set();
    talleres.forEach((t) => (t.services_offered || []).forEach((s) => set.add(s)));
    return [...set].sort();
  }, [talleres]);

  const cityOptions = useMemo(() => {
    const set = new Set();
    talleres.forEach((t) => t.location?.city && set.add(t.location.city));
    return [...set].sort();
  }, [talleres]);

  const filteredTalleres = useMemo(() => {
    let result = talleres.filter((taller) => {
      if (query) {
        const searchLower = query.toLowerCase();
        const matches =
          taller.company_name?.toLowerCase().includes(searchLower) ||
          taller.location?.city?.toLowerCase().includes(searchLower) ||
          taller.services_offered?.some((s) => s.toLowerCase().includes(searchLower));
        if (!matches) return false;
      }
      if (serviceFilter !== 'all' && !(taller.services_offered || []).includes(serviceFilter)) return false;
      if (cityFilter !== 'all' && taller.location?.city !== cityFilter) return false;
      if (ratingFilter !== 'all' && (taller.rating || 0) < parseFloat(ratingFilter)) return false;
      return true;
    });

    result.sort((a, b) => {
      if (sortBy === 'rating') return (b.rating || 0) - (a.rating || 0);
      if (sortBy === 'reviews') return (b.total_reviews || 0) - (a.total_reviews || 0);
      return (a.company_name || '').localeCompare(b.company_name || '');
    });

    return result;
  }, [talleres, query, serviceFilter, cityFilter, ratingFilter, sortBy]);

  const activeFiltersCount =
    (serviceFilter !== 'all' ? 1 : 0) + (cityFilter !== 'all' ? 1 : 0) + (ratingFilter !== 'all' ? 1 : 0) + (vehicleTypeFilter !== 'all' ? 1 : 0);

  const clearFilters = () => {
    setServiceFilter('all');
    setCityFilter('all');
    setRatingFilter('all');
    setVehicleTypeFilter('all');
  };

  const handleViewTaller = (tallerId) => {
    navigate(`/taller/${tallerId}`);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="glass-header">
        <div className="px-4 lg:px-6 py-3">
          <div className="flex justify-between items-center gap-3">
            <div className="flex items-center space-x-2 shrink-0">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="back-btn">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center space-x-2">
                <div className="w-9 h-9 bg-purple-600 rounded-xl flex items-center justify-center">
                  <Car className="w-5 h-5 text-white" />
                </div>
                <h1 className="text-xl font-bold text-slate-900 font-heading hidden sm:block">Flix</h1>
              </div>
            </div>

            {/* Search bar in header */}
            <div className="flex-1 max-w-xl">
              <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-full px-3 py-1.5 shadow-sm focus-within:ring-2 focus-within:ring-purple-500">
                <Search className="w-4 h-4 text-slate-400 shrink-0" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Servicio, ciudad o nombre del taller..."
                  className="border-0 focus-visible:ring-0 shadow-none h-8 text-sm"
                  data-testid="search-input"
                />
                {query && (
                  <button onClick={() => setQuery('')} className="text-slate-400 hover:text-slate-600" data-testid="clear-search-btn">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            <Button onClick={() => navigate('/auth')} size="sm" className="rounded-full bg-purple-600 hover:bg-purple-700 shrink-0" data-testid="login-btn">
              Iniciar Sesión
            </Button>
          </div>
        </div>

        {/* Filters toolbar */}
        <div className="px-4 lg:px-6 pb-3 border-t border-slate-100 pt-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="hidden md:flex items-center gap-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider mr-1">
              <SlidersHorizontal className="w-3.5 h-3.5" /> Filtros
            </span>

            {/* Vehicle type chips */}
            <div className="flex gap-1.5">
              <button
                onClick={() => setVehicleTypeFilter('all')}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${vehicleTypeFilter === 'all' ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-slate-600 border-slate-200 hover:border-purple-300'}`}
                data-testid="vt-filter-all"
              >
                Todos
              </button>
              {Object.entries(VEHICLE_TYPE_META).map(([key, meta]) => {
                const Icon = meta.icon;
                const isActive = vehicleTypeFilter === key;
                return (
                  <button
                    key={key}
                    onClick={() => setVehicleTypeFilter(key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors inline-flex items-center gap-1 ${isActive ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-slate-600 border-slate-200 hover:border-purple-300'}`}
                    data-testid={`vt-filter-${key}`}
                  >
                    <Icon className="w-3.5 h-3.5" /> {meta.label}
                  </button>
                );
              })}
            </div>

            <div className="w-px h-6 bg-slate-200 hidden md:block" />

            <Select value={serviceFilter} onValueChange={setServiceFilter}>
              <SelectTrigger className="w-[170px] h-8 text-xs rounded-full border-slate-200 bg-white" data-testid="filter-service">
                <SelectValue placeholder="Servicio" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los servicios</SelectItem>
                {serviceOptions.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={cityFilter} onValueChange={setCityFilter}>
              <SelectTrigger className="w-[150px] h-8 text-xs rounded-full border-slate-200 bg-white" data-testid="filter-city">
                <SelectValue placeholder="Ciudad" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las ciudades</SelectItem>
                {cityOptions.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={ratingFilter} onValueChange={setRatingFilter}>
              <SelectTrigger className="w-[170px] h-8 text-xs rounded-full border-slate-200 bg-white" data-testid="filter-rating">
                <SelectValue placeholder="Calificación" />
              </SelectTrigger>
              <SelectContent>
                {RATING_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {activeFiltersCount > 0 && (
              <button
                onClick={clearFilters}
                className="text-xs font-semibold text-purple-700 hover:text-purple-900 inline-flex items-center gap-1"
                data-testid="clear-filters-btn"
              >
                <X className="w-3.5 h-3.5" /> Limpiar ({activeFiltersCount})
              </button>
            )}

            <div className="ml-auto flex items-center gap-2">
              <span className="text-xs text-slate-500 hidden sm:block">Ordenar:</span>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[170px] h-8 text-xs rounded-full border-slate-200 bg-white" data-testid="sort-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile view toggle */}
      <div className="lg:hidden sticky top-0 z-40 flex justify-center py-2 bg-slate-50">
        <div className="inline-flex bg-white border border-slate-200 rounded-full p-1 shadow-sm">
          <button
            onClick={() => setMobileView('list')}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 transition-colors ${mobileView === 'list' ? 'bg-purple-600 text-white' : 'text-slate-600'}`}
            data-testid="mobile-toggle-list"
          >
            <List className="w-3.5 h-3.5" /> Lista
          </button>
          <button
            onClick={() => setMobileView('map')}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 transition-colors ${mobileView === 'map' ? 'bg-purple-600 text-white' : 'text-slate-600'}`}
            data-testid="mobile-toggle-map"
          >
            <MapIcon className="w-3.5 h-3.5" /> Mapa
          </button>
        </div>
      </div>

      {/* Split layout: results + map */}
      <div className="flex-1 lg:grid lg:grid-cols-5 gap-0">
        {/* Results list */}
        <div className={`lg:col-span-3 px-4 lg:px-6 py-5 ${mobileView === 'map' ? 'hidden lg:block' : ''}`} data-testid="results-list">
          <div className="mb-4 flex items-baseline justify-between">
            <div>
              <h2 className="text-lg font-bold text-slate-900">
                {query ? `Resultados para "${query}"` : 'Talleres disponibles'}
              </h2>
              <p className="text-sm text-slate-500" data-testid="results-count">
                {filteredTalleres.length} {filteredTalleres.length === 1 ? 'taller encontrado' : 'talleres encontrados'}
              </p>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-16">
              <div className="inline-block w-10 h-10 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-slate-500 mt-4 text-sm">Cargando talleres...</p>
            </div>
          ) : filteredTalleres.length === 0 ? (
            <Card className="p-12 text-center border-dashed" data-testid="no-results">
              <MapPin className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 mb-4">No se encontraron talleres con esos criterios</p>
              {activeFiltersCount > 0 && (
                <Button variant="outline" size="sm" onClick={clearFilters} className="rounded-full">
                  Limpiar filtros
                </Button>
              )}
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredTalleres.map((taller) => (
                <Card
                  key={taller.id}
                  className={`p-5 transition-all cursor-pointer border ${hoveredId === taller.id ? 'border-purple-400 shadow-md' : 'border-slate-200 hover:border-purple-300 hover:shadow-md'}`}
                  onMouseEnter={() => setHoveredId(taller.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  onClick={() => handleViewTaller(taller.id)}
                  data-testid={`taller-card-${taller.id}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h3 className="text-lg font-bold text-slate-900">{taller.company_name}</h3>
                    <div className="flex items-center gap-1 shrink-0 bg-slate-50 border border-slate-100 rounded-full px-2.5 py-1">
                      <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                      <span className="font-bold text-sm">{taller.rating}</span>
                      <span className="text-slate-400 text-xs">({taller.total_reviews})</span>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500 mb-3">
                    <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {taller.location?.city} · zona aproximada</span>
                    {taller.phone && <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {taller.phone}</span>}
                    <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Responde en &lt;24h</span>
                  </div>

                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {taller.vehicle_types?.map((vt) => {
                      const meta = VEHICLE_TYPE_META[vt];
                      if (!meta) return null;
                      const Icon = meta.icon;
                      return (
                        <Badge key={vt} variant="outline" className="text-xs bg-slate-50 text-slate-600 border-slate-200">
                          <Icon className="w-3 h-3 mr-1" /> {meta.label}
                        </Badge>
                      );
                    })}
                    {taller.services_offered?.slice(0, 4).map((service, idx) => (
                      <span key={idx} className="px-2.5 py-0.5 bg-purple-50 text-purple-700 border border-purple-100 rounded-full text-xs font-medium">
                        {service}
                      </span>
                    ))}
                    {(taller.services_offered?.length || 0) > 4 && (
                      <span className="px-2.5 py-0.5 text-slate-400 text-xs">+{taller.services_offered.length - 4} más</span>
                    )}
                  </div>

                  <div className="flex gap-2 justify-end">
                    <Button
                      onClick={(e) => { e.stopPropagation(); handleViewTaller(taller.id); }}
                      variant="outline"
                      size="sm"
                      className="rounded-full border-slate-300 hover:border-purple-400 hover:text-purple-700"
                      data-testid={`view-details-btn-${taller.id}`}
                    >
                      Ver Detalles
                    </Button>
                    <Button
                      onClick={(e) => { e.stopPropagation(); navigate('/auth'); }}
                      size="sm"
                      className="rounded-full bg-purple-600 hover:bg-purple-700"
                      data-testid={`contact-btn-${taller.id}`}
                    >
                      Solicitar Cotización
                    </Button>
                  </div>
                </Card>
              ))}

              {/* CTA */}
              <Card className="p-8 text-center bg-white border border-slate-200">
                <h3 className="text-xl font-bold mb-2">¿Quieres solicitar un servicio?</h3>
                <p className="text-sm text-slate-600 mb-5">
                  Crea una cuenta gratis y recibe ofertas personalizadas de estos talleres
                </p>
                <Button
                  onClick={() => navigate('/auth')}
                  className="rounded-full bg-purple-600 hover:bg-purple-700 px-8"
                  data-testid="cta-signup-btn"
                >
                  Registrarse Gratis
                </Button>
              </Card>
            </div>
          )}
        </div>

        {/* Map (sticky on desktop) */}
        <div className={`lg:col-span-2 ${mobileView === 'list' ? 'hidden lg:block' : ''}`}>
          <div className="lg:sticky lg:top-[130px] p-4 lg:pl-0 lg:pr-6 h-[70vh] lg:h-[calc(100vh-150px)]">
            <TalleresMap
              talleres={filteredTalleres}
              hoveredId={hoveredId}
              onViewTaller={handleViewTaller}
              height="100%"
            />
            <p className="text-[11px] text-slate-400 text-center mt-2 flex items-center justify-center gap-1">
              <MapPin className="w-3 h-3" /> Los círculos muestran la zona aproximada de cada taller — la dirección exacta se comparte al agendar
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchPage;
